import {
  IAdminReportsData,
  PermissionsOfAdmin,
  PharmacistLegendVariant,
  PharmacyLegendVariant,
  ServiceTypes,
  TimeFormat,
  TypeOfUser,
  WorkshiftReportStatus,
  convertToDecimals,
} from '@pharmaplan/common';
import ServiceHandler, {
  idStore,
} from '@pharmaplan/common/helpers/ServiceHandler';
import strings from '../../../localization';
import { IUserProfileLinkSignature } from '../../../hooks/Admin/useUserProfileLink';
import {
  DynamicTableCell,
  nameLabels,
  OtherScreens,
  PharmacyWorkshiftStatusLabels,
  ReplacementTypes,
  UserLabels,
} from '../../../helpers/Constants';

import requestCancellationDisabledIcon from '../../../assets/svg/requestCancellationDisabled.svg';
import expressBookingDisabledIcon from '../../../assets/svg/disableExpressBooking.svg';
import findMatchingDisabledIcon from '../../../assets/svg/disableFindMatching.svg';
import approveBookingDisabledIcon from '../../../assets/svg/disabledApprove.svg';
import requestCancellationIcon from '../../../assets/svg/requestCancellation.svg';
import { IShowCancellationParams } from '../../../hooks/Admin/useWorkshiftDelete';
import deleteRequestDisabledIcon from '../../../assets/svg/disableDelete.svg';
import { dateRecord, rateRecord } from '../../../helpers/longTermRecord';
import expressBookingIcon from '../../../assets/svg/expressBooking.svg';
import approveBookingIcon from '../../../assets/svg/approveBooking.svg';
import { getDayMonthDateYear, hasAction } from '../../Reports/helpers';
import generateInvoice from '../../../assets/svg/generateInvoice.svg';
import editDisabledIcon from '../../../assets/svg/editDisabled.svg';
import findMatchingIcon from '../../../assets/svg/findMatching.svg';
import { getHoursDiff } from '../../Modals/WorkshiftDetails/utils';
import deleteRequestIcon from '../../../assets/svg/delete.svg';
import { currencyFormatter, timeString } from '../../../helpers/Functions';
import editIcon from '../../../assets/svg/edit.svg';
import viewBookingsIcon from '../../../assets/svg/showPassword.svg';

import styles from './style';

const { Delete, Booking } = PermissionsOfAdmin;
const { self, full } = ServiceTypes;
const isFullService = ServiceHandler.getService() === full;

interface IAdminReportsHeader {
  key: string;
  label: string;
  sortingKey?: string;
}

interface IBookingReportsGeneric {
  data: IAdminReportsData[];
  goToUserProfile: IUserProfileLinkSignature;
  timeFormat: TimeFormat;
  can?: (param: PermissionsOfAdmin) => boolean;
}

interface IBookingReportsRow extends IBookingReportsGeneric {
  getImage: (logoId: string) => string;
  showExpressBooking: (bookingId: string, workshift?: boolean) => void;
  showRequestCancellation: (params: IShowCancellationParams) => void;
  downloadChargesReport: (paymentId: string) => () => void;
  downloadProfessionalInvoice: (
    paymentId: string,
    professionalId: string,
  ) => () => void;
}

interface IAvailabilityReportsRow extends IBookingReportsGeneric {
  findMatchingWorkshifts: (
    availabilityId: string,
    code: string,
    pharmacistId: string,
  ) => void;
  showDeletePreSubmit: (availabilityId: string, dateTime: string) => void;
}

interface IPostedWorkshiftsReportRow extends IBookingReportsGeneric {
  getImage: (logoId: string) => string;
  showExpressBooking: (bookingId: string, workshift?: boolean) => void;
  findMatchingAvailabilities: (workshiftId: string) => void;
  showWorkshiftDelete: (
    workshiftId: string,
    dateTime: string,
    longTermShiftContractId?: string,
  ) => void;
  showEditWorkshift: (workshiftId: string) => void;
  selectedIds: Array<string>;
  handlePress: (id: string, customCount?: number) => void;
}

interface IOwnershipReportsRow {
  data: IAdminReportsData[];
  showAssignedAccounts: (adminUserId: string, adminName: string) => void;
  goToOwnershipBookings: (ownerId: string) => () => void;
}

interface IRequestedReportsRow extends IBookingReportsGeneric {
  getImage: (logoId: string) => string;
  goToApproveBooking: ({
    bookingId,
    workshiftId,
    fromScreen,
    longTermShiftContractId,
    eventIds,
    professionalId,
  }: {
    bookingId: string;
    workshiftId: string;
    fromScreen: OtherScreens;
    longTermShiftContractId?: string;
    eventIds?: Array<string> | null;
    professionalId?: string;
  }) => () => void;
}

const { text, customLink, icon: img, bar, chip, checkbox } = DynamicTableCell;

enum Actions {
  requestCancellation = 'requestCancellation',
  expressBooking = 'expressBooking',
  editBooking = 'editBooking',
  findMatching = 'findMatching',
  matchingAvailabilities = 'matchingAvailabilities',
  approve = 'approve',
  delete = 'delete',
  editWorkshift = 'editWorkshift',
}

const statusStyleMap = {
  [WorkshiftReportStatus.posted]: styles.posted,
  [WorkshiftReportStatus.booked]: styles.booked,
  [WorkshiftReportStatus.request]: styles.booked,
  [WorkshiftReportStatus.unfulfilled]: styles.free,
  [WorkshiftReportStatus.pariallyUnfulfilled]: styles.free,
  [WorkshiftReportStatus.partiallyRequested]: styles.booked,
  [WorkshiftReportStatus.partiallyRequestedBooked]: styles.booked,
  [WorkshiftReportStatus.partiallyBooked]: styles.booked,
};

export const dateAndTime = (
  selectedDate: string,
  sDate: string,
  eDate: string,
  timeFormat: TimeFormat,
) => {
  const formattedDate = getDayMonthDateYear(selectedDate);
  const time = timeString(sDate, eDate, timeFormat);
  const dateTime = `${formattedDate} | ${time}`;

  return dateTime;
};

const ActionTooltips = {
  [Actions.requestCancellation]: {
    enabled: isFullService
      ? strings.requestCancellation
      : strings.cancelBooking,
    disabled: strings.outOfDate,
  },
  [Actions.editBooking]: { enabled: strings.edit, disabled: strings.outOfDate },
  [Actions.delete]: { enabled: strings.delete, disabled: strings.outOfDate },
  [Actions.expressBooking]: {
    enabled: strings.expressBooking,
    disabled: strings.outOfDate,
  },
  [Actions.approve]: { enabled: strings.approve, disabled: strings.outOfDate },
  [Actions.findMatching]: {
    enabled: strings.findMatchingWorkshifts,
    disabled: strings.outOfDate,
  },
  [Actions.matchingAvailabilities]: {
    enabled: strings.matchingAvailabilities,
    disabled: strings.outOfDate,
  },
  [Actions.editWorkshift]: {
    enabled: strings.editWorkshift,
    disabled: strings.outOfDate,
  },
};

const ActionIcons = {
  [Actions.requestCancellation]: {
    enabled: requestCancellationIcon,
    disabled: requestCancellationDisabledIcon,
  },
  [Actions.editBooking]: { enabled: editIcon, disabled: editDisabledIcon },
  [Actions.delete]: {
    enabled: deleteRequestIcon,
    disabled: deleteRequestDisabledIcon,
  },
  [Actions.expressBooking]: {
    enabled: expressBookingIcon,
    disabled: expressBookingDisabledIcon,
  },
  [Actions.approve]: {
    enabled: approveBookingIcon,
    disabled: approveBookingDisabledIcon,
  },
  [Actions.findMatching]: {
    enabled: findMatchingIcon,
    disabled: findMatchingDisabledIcon,
  },
  [Actions.matchingAvailabilities]: {
    enabled: findMatchingIcon,
    disabled: findMatchingDisabledIcon,
  },
  [Actions.editWorkshift]: {
    enabled: editIcon,
    disabled: editDisabledIcon,
  },
};

const handleIcon = (action: Actions, date: string, isBooked = false) => {
  const isDisabled = !hasAction(date);

  const icon = isDisabled || isBooked
    ? ActionIcons[action].disabled
    : ActionIcons[action].enabled;
  const tooltip = isDisabled
    ? ActionTooltips[action].disabled
    : ActionTooltips[action].enabled;

  return { icon, tooltip };
};

export const bookingReportsHeader = () =>
  [
    {
      key: '0',
      label: strings.banner,
    },
    {
      key: '1',
      label: strings.pharmacy,
      sortingKey: 'pharmacyName',
    },
    {
      key: '2',
      label: strings.wsCode,
      sortingKey: 'code',
    },
    {
      key: '3',
      label: `${strings.date} | ${strings.time}`,
      sortingKey: 'selectedDate',
    },
    {
      key: '4',
      label: strings.hours,
      isCenterAligned: true,
      sortingKey: 'hours',
    },
    {
      key: '5',
      label: strings.replacement,
      sortingKey: isFullService ? 'pharmacistName' : 'professionalName',
    },
    {
      key: '6',
      label: strings.type,
      sortingKey: 'replacementType',
    },
    {
      key: '7',
      label: strings.formatString(strings.hourlyRateFormat, strings.professional) as string,
      sortingKey: 'pharmacistHourlyRate',
      isCenterAligned: true,
    },
    isFullService && {
      key: '8',
      label: strings.pharmacyHourlyRate,
      sortingKey: 'pharmacyHourlyRate',
      isCenterAligned: true,
    },
    {
      key: '9',
      label: strings.action,
    },
  ].filter(Boolean) as Array<IAdminReportsHeader>;

export const requestedReportsHeader = () =>
  [
    {
      key: '1',
      label: strings.banner,
    },
    {
      key: '2',
      label: strings.pharmacy,
      sortingKey: 'pharmacyName',
    },
    {
      key: '3',
      label: strings.wsCode,
      sortingKey: 'code',
    },
    {
      key: '4',
      label: `${strings.date} | ${strings.time}`,
      sortingKey: 'selectedDate',
    },
    {
      key: '5',
      label: strings.hours,
      isCenterAligned: true,
      sortingKey: 'hours',
    },
    {
      key: '6',
      label: strings.replacement,
      sortingKey: 'pharmacistName',
    },
    {
      key: '7',
      label: strings.type,
      sortingKey: 'replacementType',
    },
    {
      key: '8',
      label: strings.formatString(strings.hourlyRateFormat, strings.professional) as string,
      sortingKey: 'pharmacistHourlyRate',
    },
    isFullService && {
      key: '9',
      label: strings.pharmacyHourlyRate,
      sortingKey: 'pharmacyHourlyRate',
    },
    {
      key: '10',
      label: strings.action,
    },
  ].filter(Boolean) as Array<IAdminReportsHeader>;

export const availabilityReportsHeader = () =>
  [
    {
      key: '0',
      label: strings.code,
      sortingKey: 'code',
    },
    {
      key: '1',
      label: `${strings.date} | ${strings.time}`,
      sortingKey: 'pharmacistName',
    },
    {
      key: '2',
      label: strings.hours,
      isCenterAligned: true,
      sortingKey: 'hours',
    },
    {
      key: '3',
      label: nameLabels().roleLabel,
      sortingKey: 'pharmacistName',
    },
    {
      key: '4',
      label: strings.type,
      sortingKey: 'userType',
    },
    {
      key: '5',
      label: strings.status,
      sortingKey: 'availabilityStatus',
    },
    {
      key: '6',
      label: strings.action,
    },
  ];

export const postedWorkshiftsHeader = () =>
  [
    {
      key: '0',
      label: strings.banner,
    },
    {
      key: '1',
      label: strings.pharmacy,
      sortingKey: 'pharmacyName',
    },
    {
      key: '2',
      label: strings.city,
      sortingKey: 'city',
    },
    {
      key: '3',
      label: strings.wsCode,
      sortingKey: 'code',
    },
    {
      key: '4',
      label: `${strings.date} | ${strings.time}`,
      sortingKey: 'selectedDate',
    },
    {
      key: '5',
      label: strings.hours,
      isCenterAligned: true,
      sortingKey: 'hours',
    },
    {
      key: '6',
      label: strings.replacementType,
      sortingKey: 'replacementType',
    },
    {
      key: '7',
      label: strings.status,
      sortingKey: 'status',
    },
    {
      key: '9',
      label: strings.formatString(strings.hourlyRateFormat, strings.professional) as string,
      sortingKey: 'pharmacistHourlyRate',
    },
    isFullService && {
      key: '8',
      label: strings.pharmacyHourlyRate,
      sortingKey: 'pharmacyHourlyRate',
    },
    {
      key: '10',
      label: strings.action,
    },
    {
      key: '11',
      label: strings.deleteRecords,
    },
  ].filter(Boolean) as Array<IAdminReportsHeader>;

export const ownershipReportsHeader = () =>
  [
    {
      key: '0',
      label: strings.adminName,
      sortingKey: 'adminName',
    },
    {
      key: '1',
      label: strings.numberOfAccounts,
      isCenterAligned: true,
      sortingKey: 'ownedAccountsCount',
    },
    {
      key: '2',
      label: strings.numberOfBookings,
      isCenterAligned: true,
      sortingKey: 'bookingsCount',
    },
    {
      key: '3',
      label: strings.totalBookedHours,
      isCenterAligned: true,
      sortingKey: 'bookedHours',
    },
    {
      key: '4',
      label: strings.totalPaid,
      isCenterAligned: true,
      sortingKey: 'amountPaid',
    },
    {
      key: '5',
      label: strings.totalCharged,
      isCenterAligned: true,
      sortingKey: 'amountCharged',
    },
    {
      key: '6',
      label: strings.action,
    },
  ];

export const ownershipReportsRow = ({
  data,
  showAssignedAccounts,
  goToOwnershipBookings,
}: IOwnershipReportsRow) =>
  data.map((item) => {
    const {
      adminUserId = '',
      adminName = '',
      ownedAccountsCount = 0,
      bookingCount = 0,
      bookedHours = 0,
      amountPaid = 0,
      amountCharged = 0,
    } = item ?? {};

    return {
      key: adminUserId,
      data: [
        {
          key: `0-${adminUserId}`,
          value: adminName,
          type: text,
          maxWidth: 200,
        },
        {
          key: `1-${adminUserId}`,
          value: {
            label: `${ownedAccountsCount}`,
            handlePress: () =>
              showAssignedAccounts(adminUserId, adminName),
          },
          type: customLink,
          isCenterAligned: true,
        },
        {
          key: `2-${adminUserId}`,
          value: `${bookingCount}`,
          type: text,
          isCenterAligned: true,
        },
        {
          key: `3-${adminUserId}`,
          value: convertToDecimals(bookedHours),
          type: text,
          isCenterAligned: true,
        },
        {
          key: `4-${adminUserId}`,
          value: currencyFormatter(amountPaid),
          type: text,
          isCenterAligned: true,
        },
        {
          key: `5-${adminUserId}`,
          value: currencyFormatter(amountCharged),
          type: text,
          isCenterAligned: true,
        },
        {
          key: `6-${adminUserId}`,
          show: true,
          value: [
            {
              key: '1',
              icon: viewBookingsIcon,
              tooltip: strings.viewBookings,
              onClick: goToOwnershipBookings(adminUserId),
            },
          ],
          type: bar,
        },
      ],
    };
  });

export const bookingReportsRow = ({
  data,
  getImage,
  goToUserProfile,
  timeFormat,
  can,
  showExpressBooking,
  showRequestCancellation,
  downloadChargesReport,
  downloadProfessionalInvoice,
}: IBookingReportsRow) =>
  data.map((item) => {
    const {
      code,
      bookingId,
      logoId,
      pharmacyHourlyRate,
      replacementType,
      pharmacyName,
      pharmacistName,
      startDate,
      endDate,
      pharmacyId,
      hours,
      pharmacistId,
      pharmacistHourlyRate,
      professionalId,
      weekDayPaidHourlyRate,
      weekendPaidHourlyRate,
      pharmacyWeekDayPaidHourlyRate = 0,
      pharmacyWeekendPaidHourlyRate = 0,
      externalPharmacistId,
      longTermShiftContractId,
      paymentId,
    } = item ?? {};

    const actionDate = longTermShiftContractId ? endDate : startDate;
    const disabled = !hasAction(actionDate);
    const date = getDayMonthDateYear(startDate);
    const time = timeString(startDate, endDate, timeFormat);
    const id = item[idStore.id as keyof typeof item] as string;

    const dateTime = `${date} | ${time}`;
    const canViewExpressBooking = can?.(Booking) && !externalPharmacistId;

    return {
      key: bookingId,
      data: [
        {
          key: `0-${bookingId}`,
          value: getImage(logoId),
          type: img,
        },
        {
          key: `1-${bookingId}`,
          value: {
            label: pharmacyName,
            handlePress: goToUserProfile({
              personnelId: pharmacyId,
              userType: TypeOfUser.pharmacy,
              fromScreen: OtherScreens.AdminReports,
              isExternal: !!externalPharmacistId,
            }),
          },
          type: customLink,
          maxWidth: 150,
        },
        {
          key: `3-${bookingId}`,
          value: code,
          type: text,
        },
        {
          ...dateRecord(
            { startDate, endDate, timeFormat },
            PharmacistLegendVariant.booked,
            longTermShiftContractId,
            id,
          ),
          maxWidth: 200,
        },
        {
          key: `5-${bookingId}`,
          value: convertToDecimals(hours),
          type: text,
          isCenterAligned: true,
        },
        {
          key: `6-${bookingId}`,
          value: {
            label: pharmacistName,
            handlePress: goToUserProfile({
              personnelId: isFullService || externalPharmacistId ? pharmacistId : professionalId,
              userType: TypeOfUser.pharmacist,
              fromScreen: OtherScreens.AdminReports,
              isExternal: !!externalPharmacistId,
            }),
          },
          type: customLink,
        },
        {
          key: `7-${bookingId}`,
          value: ReplacementTypes()[replacementType],
          type: text,
        },
        {
          ...rateRecord(
            {
              weekDayPaidHourlyRate,
              weekendPaidHourlyRate,
              rate: pharmacistHourlyRate,
            },
            longTermShiftContractId,
          ),
          key: '9~',
          isCenterAligned: true,
        },
        isFullService && {
          ...rateRecord(
            {
              weekDayPaidHourlyRate: pharmacyWeekDayPaidHourlyRate,
              weekendPaidHourlyRate: pharmacyWeekendPaidHourlyRate,
              rate: pharmacyHourlyRate,
            },
            longTermShiftContractId,
          ),
          key: `10-${bookingId}`,
          isCenterAligned: true,
        },
        {
          key: `11-${bookingId}`,
          value: [
            {
              key: '1',
              icon: handleIcon(Actions.requestCancellation, actionDate).icon,
              tooltip: handleIcon(Actions.requestCancellation, actionDate)
                .tooltip,
              disabled,
              onClick: () =>
                showRequestCancellation({
                  bookingId,
                  dateTime,
                  professionalId: isFullService ? pharmacistId : professionalId,
                  longTermShiftContractId,
                }),
            },
            canViewExpressBooking
              && !longTermShiftContractId && {
              key: '2',
              icon: handleIcon(Actions.editBooking, actionDate).icon,
              tooltip: handleIcon(Actions.editBooking, actionDate).tooltip,
              disabled,
              onClick: () => {
                showExpressBooking(bookingId);
              },
            },
            paymentId && {
              key: '3',
              tooltip: strings.charges,
              icon: generateInvoice,
              onClick: downloadChargesReport(paymentId ?? ''),
            },
            disabled && {
              key: '4',
              tooltip: strings.invoice,
              icon: generateInvoice,
              onClick: downloadProfessionalInvoice(
                bookingId ?? '',
                professionalId,
              ),
            },
          ].filter(Boolean),
          type: bar,
        },
      ].filter(Boolean),
    };
  });

export const availabilitesReportsRow = ({
  data,
  goToUserProfile,
  timeFormat,
  findMatchingWorkshifts,
  showDeletePreSubmit,
  can,
}: IAvailabilityReportsRow) =>
  data.map((item) => {
    const {
      code,
      availableTimeId,
      pharmacistName,
      professionalName,
      startDate,
      availabilityStatus,
      endDate,
      selectedDate,
      pharmacistId,
      professionalId,
      userType,
    } = item ?? {};

    const pharmacistConfig = {
      [self]: { name: professionalName, id: professionalId },
      [full]: { name: pharmacistName, id: pharmacistId },
    };

    const { name, id } = pharmacistConfig[ServiceHandler.getService()];

    const statusStyle = availabilityStatus ? styles.booked : styles.free;
    const status = availabilityStatus ? strings.booked : strings.free;
    const disabled = !hasAction(startDate) || availabilityStatus;
    const canDelete = can?.(Delete);

    const dateTime = dateAndTime(selectedDate, startDate, endDate, timeFormat);

    return {
      key: availableTimeId,
      data: [
        {
          key: `0-${availableTimeId}`,
          value: code,
          type: text,
        },
        {
          key: `1-${availableTimeId}`,
          value: dateTime,
          type: text,
        },
        {
          key: `2-${availableTimeId}`,
          value: getHoursDiff(startDate, endDate),
          type: text,
          isCenterAligned: true,
        },
        {
          key: `3-${availableTimeId}`,
          value: {
            label: name,
            handlePress: goToUserProfile({
              personnelId: id,
              userType: TypeOfUser.pharmacist,
            }),
          },
          type: customLink,
        },
        {
          key: `4-${availableTimeId}`,
          value: UserLabels()[userType],
          type: text,
        },
        {
          key: `5-${availableTimeId}`,
          value: {
            label: status,
            style: statusStyle,
          },
          type: chip,
        },
        {
          key: `6-${availableTimeId}`,
          value: [
            {
              key: '1',
              icon: handleIcon(
                Actions.findMatching,
                startDate,
                availabilityStatus,
              ).icon,
              tooltip: handleIcon(
                Actions.findMatching,
                startDate,
                availabilityStatus,
              ).tooltip,
              disabled,
              onClick: () =>
                findMatchingWorkshifts(availableTimeId, code, id),
            },
            canDelete && {
              key: '2',
              icon: handleIcon(Actions.delete, startDate, availabilityStatus)
                .icon,
              tooltip: handleIcon(Actions.delete, startDate, availabilityStatus)
                .tooltip,
              disabled,
              onClick: () =>
                showDeletePreSubmit(availableTimeId, dateTime),
            },
          ].filter(Boolean),
          type: bar,
        },
      ],
    };
  });

export const requestedReportsRow = ({
  data,
  getImage,
  goToUserProfile,
  timeFormat,
  goToApproveBooking,
}: IRequestedReportsRow) =>
  data.map((item) => {
    const {
      code,
      bookingId,
      logoId,
      pharmacyHourlyRate,
      pharmacistHourlyRate,
      replacementType,
      pharmacyName,
      pharmacistName,
      startDate,
      endDate,
      longTermShiftContractId,
      workshiftId,
      pharmacistId,
      pharmacyId,
      hours,
      externalPharmacistId,
      weekDayPaidHourlyRate,
      weekendPaidHourlyRate,
      pharmacyWeekDayPaidHourlyRate = 0,
      pharmacyWeekendPaidHourlyRate = 0,
    } = item ?? {};

    const actionDate = longTermShiftContractId ? endDate : startDate;
    const disabled = !hasAction(actionDate);
    const id = item[idStore.id as keyof typeof item] as string;

    return {
      key: bookingId,
      data: [
        {
          key: `0-${bookingId}`,
          value: getImage(logoId),
          type: img,
        },
        {
          key: `1-${bookingId}`,
          value: {
            label: pharmacyName,
            handlePress: goToUserProfile({
              personnelId: pharmacyId,
              userType: TypeOfUser.pharmacy,
              fromScreen: OtherScreens.AdminReports,
              isExternal: !!externalPharmacistId,
            }),
          },
          maxWidth: 150,
          type: customLink,
        },
        {
          key: `3-${bookingId}`,
          value: code,
          type: text,
        },
        dateRecord(
          { startDate, endDate, timeFormat },
          PharmacyLegendVariant.requested,
          longTermShiftContractId,
          id,
        ),
        {
          key: `5-${bookingId}`,
          value: convertToDecimals(hours),
          type: text,
          isCenterAligned: true,
        },
        {
          key: `6-${bookingId}`,
          value: {
            label: pharmacistName,
            handlePress: goToUserProfile({
              personnelId: pharmacistId,
              userType: TypeOfUser.pharmacist,
              fromScreen: OtherScreens.AdminReports,
              isExternal: !!externalPharmacistId,
            }),
          },
          maxWidth: 150,
          type: customLink,
        },
        {
          key: `7-${bookingId}`,
          value: ReplacementTypes()[replacementType],
          type: text,
        },

        {
          ...rateRecord(
            {
              weekDayPaidHourlyRate,
              weekendPaidHourlyRate,
              rate: pharmacistHourlyRate,
            },
            longTermShiftContractId,
          ),
          key: '9~',
          isCenterAligned: true,
        },
        isFullService && {
          ...rateRecord(
            {
              weekDayPaidHourlyRate: pharmacyWeekDayPaidHourlyRate,
              weekendPaidHourlyRate: pharmacyWeekendPaidHourlyRate,
              rate: pharmacyHourlyRate,
            },
            longTermShiftContractId,
          ),
          isCenterAligned: true,
        },
        {
          key: `11-${bookingId}`,
          value: [
            !externalPharmacistId && {
              key: '1',
              icon: handleIcon(Actions.approve, actionDate).icon,
              tooltip: handleIcon(Actions.approve, actionDate).tooltip,
              disabled,
              onClick: goToApproveBooking({
                bookingId,
                workshiftId,
                fromScreen: OtherScreens.AdminRequestedReports,
                longTermShiftContractId,
                professionalId: id,
              }),
            },
          ],
          type: bar,
        },
      ].filter(Boolean),
    };
  });

export const postedWorkshiftsReportRow = ({
  data,
  getImage,
  goToUserProfile,
  timeFormat,
  findMatchingAvailabilities,
  can,
  showExpressBooking,
  showWorkshiftDelete,
  showEditWorkshift,
  selectedIds,
  handlePress,
}: IPostedWorkshiftsReportRow) =>
  data.map((item) => {
    const {
      code,
      workshiftId,
      logoId,
      pharmacyHourlyRate,
      pharmacistHourlyRate,
      replacementType,
      pharmacyName,
      startDate,
      endDate,
      selectedDate,
      longTermShiftContractId,
      weekDayPaidHourlyRate,
      weekendPaidHourlyRate,
      pharmacyWeekDayPaidHourlyRate = 0,
      pharmacyWeekendPaidHourlyRate = 0,
      hours,
      status,
      pharmacyId,
      city,
      totalShiftCount = 0,
      bookedShiftCount = 0,
    } = item ?? {};

    const actionDate = longTermShiftContractId ? endDate : startDate;
    const disabled = !hasAction(actionDate);
    const canDelete = can?.(Delete);
    const canViewExpressBooking = can?.(Booking) && !longTermShiftContractId;
    const postedCount = totalShiftCount - bookedShiftCount;

    return {
      key: workshiftId,
      data: [
        {
          key: `0-${workshiftId}`,
          value: getImage(logoId),
          type: img,
        },
        {
          key: `1-${workshiftId}`,
          value: {
            label: pharmacyName,
            handlePress: goToUserProfile({
              personnelId: pharmacyId,
              userType: TypeOfUser.pharmacy,
              fromScreen: OtherScreens.AdminReports,
            }),
          },
          maxWidth: 150,
          type: customLink,
        },
        {
          key: `2-${workshiftId}`,
          value: city,
          type: text,
        },
        {
          key: `3-${workshiftId}`,
          value: code,
          type: text,
        },
        dateRecord(
          { startDate, endDate, timeFormat },
          PharmacistLegendVariant.workshift,
          longTermShiftContractId,
        ),
        {
          key: `5-${workshiftId}`,
          value: convertToDecimals(hours),
          type: text,
          isCenterAligned: true,
        },
        {
          key: `6-${workshiftId}`,
          value: ReplacementTypes()[replacementType],
          type: text,
        },
        {
          key: `7-${workshiftId}`,
          value: {
            label: PharmacyWorkshiftStatusLabels()[status],
            style: statusStyleMap[status],
          },
          type: chip,
        },
        {
          ...rateRecord(
            {
              weekDayPaidHourlyRate,
              weekendPaidHourlyRate,
              rate: pharmacistHourlyRate,
            },
            longTermShiftContractId,
          ),
          key: '9~',
          isCenterAligned: true,
        },
        isFullService && {
          ...rateRecord(
            {
              weekDayPaidHourlyRate: pharmacyWeekDayPaidHourlyRate,
              weekendPaidHourlyRate: pharmacyWeekendPaidHourlyRate,
              rate: pharmacyHourlyRate,
            },
            longTermShiftContractId,
          ),
          isCenterAligned: true,
        },
        {
          key: `10-${workshiftId}`,
          value: [
            !longTermShiftContractId && {
              key: '1',
              icon: handleIcon(Actions.matchingAvailabilities, actionDate).icon,
              tooltip: handleIcon(Actions.matchingAvailabilities, actionDate)
                .tooltip,
              disabled,
              onClick: () => {
                findMatchingAvailabilities(workshiftId);
              },
            },
            {
              key: '4',
              icon: handleIcon(Actions.editWorkshift, actionDate).icon,
              tooltip: handleIcon(Actions.editWorkshift, actionDate).tooltip,
              disabled,
              onClick: () => {
                showEditWorkshift(workshiftId);
              },
            },
            canDelete && {
              key: '2',
              icon: handleIcon(Actions.delete, actionDate).icon,
              tooltip: handleIcon(Actions.delete, actionDate).tooltip,
              disabled,
              onClick: () => {
                showWorkshiftDelete(
                  workshiftId,
                  dateAndTime(selectedDate, startDate, endDate, timeFormat),
                  longTermShiftContractId,
                );
              },
            },
            canViewExpressBooking && {
              key: '3',
              icon: expressBookingIcon,
              tooltip: strings.expressBooking,
              disabled,
              onClick: () =>
                showExpressBooking(workshiftId, true),
            },
          ].filter(Boolean),
          type: bar,
        },
        canDelete && {
          key: `11-${workshiftId}`,
          value: {
            checked: !!selectedIds?.includes(workshiftId),
            handlePress: handlePress(workshiftId, postedCount),
            disabled,
            tooltipText: strings.outOfDate,
          },
          type: checkbox,
        },
      ].filter(Boolean),
    };
  });
